import React, { useEffect, useRef, useState } from 'react';
import { loadReCaptcha } from 'react-recaptcha-v3';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import root from 'react-shadow/styled-components';
import { Normalize } from 'styled-normalize';
import {
  MemoryRouter as SingleRoute,
  Route,
} from 'react-router-dom';
import Bugsnag from '@bugsnag/js';
import Store from './configureStore';
import { siteKey } from './app/apis';
import { setup as CssLoadedWrapper } from './styles';
import Setup, { bpOnlySetup } from './bootsrap';
import Router from './router';
import { ErrorFallback } from './pages/ErrorBoundary';
import { BulkPickup } from './pages';

/**
 * Define store and persistor objects
 */
const { store, persistor } = Store;

const App = ({ pluginId = '' }) => {
  const [isServerConnectionFailed, setIsServerConnectionFailed] = useState(false);
  const [isDataCaptured, setIsDataCaptured] = useState(false);
  const containerRef = useRef();
  const searchParams = new URLSearchParams(window.location.search);
  const isBpOnly = searchParams.get('feature') === 'bulk_pickup';
  const [errorMessage, setErrorMessage] = useState('');

  const dontLoadWebapp = () => setIsServerConnectionFailed(true);

  const loadWebapp = () => setIsDataCaptured(true);

  useEffect(() => {
    loadReCaptcha(siteKey);
    // Bugsnag.notify(new Error('Bugsnag test error from WA front end'));
  }, []);
  /**
   * Right after state rehydrates
   */
  const onBeforeLift = async () => {
    // Lifted
    try {
      /**
       * App initial Setup
       */
      let result = '';

      if (isBpOnly) {
        result = await bpOnlySetup();
      } else {
        result = await Setup(pluginId);
      }

      if (result !== 'done') {
        const errorMessage = `Could not bootstrap the application - Error with Setup function plugin_id=${pluginId} | Error: ${JSON.stringify(result)}`;
        setErrorMessage(errorMessage);
        dontLoadWebapp();
        if (pluginId) {
          Bugsnag.notify(new Error(errorMessage));
        }
        throw new Error(errorMessage);
      }

      return loadWebapp();
    } catch (e) {
      const errorMessage = `Could not bootstrap the application - Error with Setup function plugin_id=${pluginId} | Error: ${JSON.stringify(e)}`;
      setErrorMessage(errorMessage);
      dontLoadWebapp();
      if (pluginId) {
        Bugsnag.notify(new Error(errorMessage));
      }
      throw new Error(errorMessage);
    }
  };

  /* Initial the script of iframe resizer */
  useEffect(() => {
    const script = document.createElement('script');
    window.iFrameResizer = {
      resizeFrom: 'child',
      heightCalculationMethod: 'lowestElement'
    };

    script.type = 'text/javascript';
    script.src = 'https://cdn.recyclecoach.com/resources/js/iframeResizer.contentWindow.min.js';
    script.async = true;

    document.body.appendChild(script);

    const rcObserver = new ResizeObserver(() => {
      if ('parentIFrame' in window) {
        window.parentIFrame.size();
      }
    });
    if (containerRef.current) {
      rcObserver.observe(containerRef.current);
    }

    return () => {
      document.body.removeChild(script);
      rcObserver.unobserve(containerRef.current);
    };
  }, [containerRef.current]);

  useEffect(() => {
    const { body } = document;
    body.style.height = 'auto';
    body.style.width = '100%';
    body.style.margin = '0';
    body.style.padding = '0';
    body.style.position = 'absolute';

    const rcroot = document.getElementById('rcroot');
    rcroot.style.position = 'relative';
    rcroot.style.overflow = 'hidden';

    // Remove the recaptcha v3 badge
    const recaptchaV3Css = '.grecaptcha-badge { display: none !important; }';
    const head = document.head || document.getElementsByTagName('head')[0];
    const style = document.createElement('style');

    head.appendChild(style);
    style.type = 'text/css';

    if (style.stylesheet) {
      // This is required for IE8 and below.
      style.stylesheet.cssText = recaptchaV3Css;
    } else {
      style.appendChild(document.createTextNode(recaptchaV3Css));
    }
  }, []);

  // Contain the whole app into a 680px width container
  return (
    <root.div ref={containerRef}>
      <div className="inner-rc important-rc" data-rc="rc" id="main-rc-el" style={{ maxWidth: 850, margin: 'auto' }}>
        <Provider
          store={store}
        >

          <PersistGate
            loading={null}
            persistor={persistor}
            onBeforeLift={onBeforeLift}
          >
            <Normalize />
            <CssLoadedWrapper>

              {isBpOnly ? (
                <SingleRoute>
                  <Route path="/" render={() => <BulkPickup />} />
                </SingleRoute>
              ) : (
                <>
                  {
                    isServerConnectionFailed
                      ? (
                        <ErrorFallback
                          error={{
                            message: 'Server connection error - Could not bootstrap the application',
                            stack: errorMessage
                          }}
                        />
                      ) : null
                  }
                  {
                    isDataCaptured
                      ? (
                        <div>
                          <Router
                            pluginId={pluginId}
                          />
                        </div>
                      ) : null
                  }
                </>
              )}
            </CssLoadedWrapper>
          </PersistGate>

          {/* Store persistor */}

        </Provider>
      </div>
    </root.div>
  );
};

export default App;
